import {
  CalendarOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { Button, DatePicker, message, Modal, Space, TimePicker } from 'antd';
import client from 'apolloClient';
import dayjs from 'dayjs';
import { REQUEST_APPOINTMENT_DATES } from 'gql/appointments/mutation';
import { GET_APPOINTMENT_ITEM } from 'gql/appointments/queries';
import { RequestAppointmentDates } from 'gql/appointments/__generated__/RequestAppointmentDates';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { FC, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { showErrorMessage } from 'utils/showErrorMessage';
import { getDisabledTime } from 'utils/date';

const initialDates = {
  date: dayjs(),
  time: null
};

const AddOptionModal: FC = () => {
  // translations
  const { t } = useTranslation();
  // states
  const [dates, setDates] = useState<
    {
      date: dayjs.Dayjs | null;
      time: dayjs.Dayjs | null;
    }[]
  >([
    {
      ...initialDates
    }
  ]);

  // context
  const { toggleModal } = useUIDispatchContext();
  const { appointmentOption } = useModalStateContext();

  const closeModal = useCallback(() => {
    toggleModal('appointmentOption', false);
    setDates([
      {
        ...initialDates
      }
    ]);
  }, [toggleModal]);

  // graphql
  const [requestDate, { loading: isRequesting }] =
    useMutation<RequestAppointmentDates>(REQUEST_APPOINTMENT_DATES, {
      onCompleted() {
        message.success(t('appointments_details.successfully_added_option'));
        client.refetchQueries({ include: [GET_APPOINTMENT_ITEM] });
        closeModal();
      },
      onError: err => showErrorMessage(err)
    });

  const appointmentId = appointmentOption.params?.id;

  const onSubmit = useCallback(() => {
    if (dates.some(item => !item.date || !item.time)) {
      return message.error(t('appointments_details.please_select_date'));
    }

    const variables = {
      id: appointmentId,
      input: {
        dates: dates.map(item => {
          const dateStr = item.date?.format('YYYY-MM-DD');
          const timeStr = item.time?.format('HH:mm');

          return dayjs(`${dateStr} ${timeStr}`).format();
        })
      }
    };

    requestDate({
      variables
    });
  }, [appointmentId, dates, requestDate, t]);

  return (
    <Modal
      open={appointmentOption.visible}
      title={t('appointments_details.add_your_option')}
      confirmLoading={isRequesting}
      onCancel={closeModal}
      onOk={onSubmit}
    >
      {dates.map((item, index, arr) => {
        return (
          <Space
            size={16}
            direction="horizontal"
            style={{
              width: '100%',
              marginBottom: 32
            }}
            key={`date-item-${index}`}
          >
            <DatePicker
              suffixIcon={
                <CalendarOutlined style={{ fontSize: 14, color: '#09121F' }} />
              }
              defaultValue={dayjs()}
              format={'MMM D, ddd'}
              placement={'bottomLeft'}
              onChange={date => {
                if (!date) {
                  return;
                }

                setDates(prev => {
                  const data = [...prev];

                  data[index].date = date;

                  return [...data];
                });
              }}
              disabledDate={current => dayjs().diff(current, 'days') > 0}
            />
            <TimePicker
              className="timepicker-main"
              format="HH:mm"
              minuteStep={30}
              showNow={false}
              allowClear={false}
              disabledTime={() => getDisabledTime(item.date)}
              hideDisabledOptions
              renderExtraFooter={() => false}
              value={item.time}
              popupClassName="add-option-popup"
              onSelect={time => {
                if (!time) {
                  return;
                }

                const minutes = time.format('mm');

                const isTimeValid = minutes === '00' || minutes === '30';

                if (!isTimeValid) {
                  return;
                }

                setDates(prev => {
                  const data = [...prev];

                  dates[index].time = time;

                  return [...data];
                });
              }}
            />
            {item.date &&
              item.time &&
              index === arr.length - 1 &&
              arr.length < 5 && (
                <Button
                  size="middle"
                  onClick={() =>
                    setDates(prev => [...prev, { ...initialDates }])
                  }
                >
                  <PlusOutlined />
                </Button>
              )}
            {index > 0 && (
              <Button
                size="middle"
                onClick={() =>
                  setDates(prev =>
                    prev.filter((_, dateIndex) => dateIndex !== index)
                  )
                }
                danger
              >
                <DeleteOutlined />
              </Button>
            )}
          </Space>
        );
      })}
    </Modal>
  );
};

export default memo(AddOptionModal);
